import { ITEMS, InventorySlot, ItemId } from '../../items'
import Bag from '../bag'

const CHEST_SIZE = 27

/**
 * Chest storage panel: click to move items between chest and bag
 */
export default class Chest {
  constructor(bag: Bag, onToggle?: (open: boolean) => void) {
    this.bag = bag
    this.onToggle = onToggle
    this.panel.className = 'chest-panel hidden'
    document.body.appendChild(this.panel)

    document.body.addEventListener('keydown', (e: KeyboardEvent) => {
      if (!this.isOpen || e.repeat) {
        return
      }
      if (e.key === 'e' || e.key === 'Escape') {
        this.close()
      }
    })
  }

  bag: Bag
  onToggle?: (open: boolean) => void
  store = new Map<string, InventorySlot[]>()
  panel = document.createElement('div')
  isOpen = false
  currentKey = ''

  open = (key: string) => {
    this.currentKey = key
    if (!this.store.has(key)) {
      this.store.set(key, new Array(CHEST_SIZE).fill(null))
    }
    this.isOpen = true
    this.bag.suppressToggle = true
    this.render()
    this.panel.classList.remove('hidden')
    this.onToggle?.(true)
  }

  close = () => {
    this.isOpen = false
    this.bag.suppressToggle = false
    this.panel.classList.add('hidden')
    this.onToggle?.(false)
  }

  // take everything out of a chest (e.g. when it is mined)
  emptyAt = (key: string) => {
    const slots = this.store.get(key) ?? []
    this.store.delete(key)
    return slots
  }

  private get slots() {
    return this.store.get(this.currentKey) ?? []
  }

  private transferToBag(index: number) {
    const slot = this.slots[index]
    if (!slot) {
      return
    }
    const left = this.addToSlots(this.bag.slots, slot.itemId, slot.count)
    this.slots[index] = left ? { itemId: slot.itemId, count: left } : null
    this.render()
    this.bag.refresh()
  }

  private transferToChest(index: number) {
    const slot = this.bag.slots[index]
    if (!slot) {
      return
    }
    const left = this.addToSlots(this.slots, slot.itemId, slot.count)
    this.bag.slots[index] = left ? { itemId: slot.itemId, count: left } : null
    this.render()
    this.bag.refresh()
  }

  // returns the count that did not fit
  private addToSlots(slots: InventorySlot[], itemId: ItemId, count: number) {
    for (const slot of slots) {
      if (slot?.itemId === itemId && slot.count < 64) {
        const accepted = Math.min(64 - slot.count, count)
        slot.count += accepted
        count -= accepted
        if (count === 0) {
          return 0
        }
      }
    }
    for (let i = 0; i < slots.length; i++) {
      if (!slots[i]) {
        const accepted = Math.min(64, count)
        slots[i] = { itemId, count: accepted }
        count -= accepted
        if (count === 0) {
          return 0
        }
      }
    }
    return count
  }

  private createSlot(slot: InventorySlot) {
    const element = document.createElement('div')
    element.className = 'item'

    if (slot) {
      const definition = ITEMS[slot.itemId]
      const img = document.createElement('img')
      img.className = 'icon'
      img.alt = definition.name
      img.title = definition.name
      img.src = definition.icon
      element.appendChild(img)

      const count = document.createElement('span')
      count.className = 'item-count'
      count.innerHTML = `${slot.count}`
      element.appendChild(count)
    }

    return element
  }

  private render() {
    this.panel.innerHTML = ''

    const title = document.createElement('div')
    title.className = 'inventory-title'
    title.innerHTML = '箱子'
    this.panel.appendChild(title)

    const chestGrid = document.createElement('div')
    chestGrid.className = 'chest-grid'
    for (let i = 0; i < CHEST_SIZE; i++) {
      const slot = this.createSlot(this.slots[i])
      slot.addEventListener('click', () => this.transferToBag(i))
      chestGrid.appendChild(slot)
    }
    this.panel.appendChild(chestGrid)

    const label = document.createElement('div')
    label.className = 'craft-label'
    label.innerHTML = '背包'
    this.panel.appendChild(label)

    const bagGrid = document.createElement('div')
    bagGrid.className = 'chest-grid'
    for (let i = 0; i < this.bag.slots.length; i++) {
      const slot = this.createSlot(this.bag.slots[i])
      slot.addEventListener('click', () => this.transferToChest(i))
      bagGrid.appendChild(slot)
    }
    this.panel.appendChild(bagGrid)
  }
}
